import { Divider, FormGroup, Switch, Typography } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { ScreenLayoutTypeEnum } from 'src/constants';
import { useGlobalComponentsScreen } from 'src/hooks/useGlobalComponentsScreen';
import { IGridOptions } from 'src/interfaces';
import {
  isCurrentScreenWithCOD,
  isCurrentScreenWithCODThankYou,
  isCurrentScreenWithDYR,
  isCurrentScreenWithDYS,
  isDYRDisabled,
  lang,
  theme,
} from 'src/utils';

import {
  calculateGridIndex,
  getCodScreenSequence,
  getRecommendationsScreenSequence,
} from './AdvancedSettings.helpers';
import type { AdvancedSettingsProps } from './AdvancedSettings.interface';
import {
  AlertMessage,
  Container,
  MarginFormControlLabel,
  NumberContainer,
} from './AdvancedSettings.styles';
import { AdvancedSettingsInfoTooltip } from './AdvancedSettingsInfoTooltip';

export const AdvancedSettings = ({
  control,
  watch,
  setValue,
  selectedChannelInfo,
  isOutdoor,
  codPosition,
}: AdvancedSettingsProps) => {
  const selectedScreenLayoutType = watch('screen_layout_type') as
    | ScreenLayoutTypeEnum
    | undefined;
  const globalComponentsScreen = useGlobalComponentsScreen(
    undefined,
    selectedChannelInfo?.screens?.length,
    selectedScreenLayoutType,
  );
  const screenCount = useMemo(
    () => selectedChannelInfo?.screens?.length ?? 0,
    [selectedChannelInfo?.screens?.length],
  );
  const codGridIndex = useMemo(() => {
    const codScreenSequence = getCodScreenSequence(globalComponentsScreen);

    return calculateGridIndex(codScreenSequence, codPosition, screenCount);
  }, [globalComponentsScreen, codPosition, screenCount]);
  const recommendationsGridIndex = useMemo(() => {
    const recommendationsScreenSequnce = getRecommendationsScreenSequence(
      globalComponentsScreen,
    );

    return calculateGridIndex(
      recommendationsScreenSequnce,
      codPosition,
      screenCount,
    );
  }, [globalComponentsScreen, codPosition, screenCount]);

  const codGrid = watch(`grid_${codGridIndex}`);
  const recommendationsGrid = watch(`grid_${recommendationsGridIndex}`);
  const disclaimers1 = watch(`disclaimers.${codGridIndex}`);
  const disclaimers2 = watch(`disclaimers.${recommendationsGridIndex}`);
  const enableCustomerOrderDisplay = watch('enableCustomerOrderDisplay');
  const enableRotation = watch('enableRotation');
  const enableTakeover = watch('enableTakeover');

  const { hasCOD, hasDYS, hasDYR, hasThankYou, disabledDYR } = useMemo(() => {
    return Array.from({ length: screenCount }).reduce<{
      hasCOD: boolean;
      hasDYS: boolean;
      hasDYR: boolean;
      hasThankYou: boolean;
      disabledDYR: boolean;
    }>(
      (acc, _, index) => {
        const screenNumber = index + 1;

        return {
          hasCOD:
            acc.hasCOD ||
            !!isCurrentScreenWithCOD(screenNumber, globalComponentsScreen),
          hasThankYou:
            acc.hasThankYou ||
            !!isCurrentScreenWithCODThankYou(
              screenNumber,
              globalComponentsScreen,
            ),
          hasDYS:
            acc.hasDYS ||
            !!isCurrentScreenWithDYS(screenNumber, globalComponentsScreen),
          hasDYR:
            acc.hasDYR ||
            !!isCurrentScreenWithDYR(screenNumber, globalComponentsScreen),
          disabledDYR:
            acc.disabledDYR || !!isDYRDisabled(globalComponentsScreen),
        };
      },
      {
        hasCOD: false,
        hasDYS: false,
        hasDYR: false,
        hasThankYou: false,
        disabledDYR: false,
      },
    );
  }, [screenCount, globalComponentsScreen]);

  // Disable Section Rotation and Takeover for 1-screen vertical or 2-screen vertical (Menu 1 + Menu 2) per COD/Confirmation rules
  const isSectionRotationAndTakeoverDisabled = useMemo(
    () =>
      screenCount === 1 ||
      (screenCount === 2 &&
        selectedScreenLayoutType === ScreenLayoutTypeEnum.TWO_MENUS),
    [screenCount, selectedScreenLayoutType],
  );

  useEffect(() => {
    if (isSectionRotationAndTakeoverDisabled && setValue) {
      setValue('enableRotation', false);
      setValue('enableTakeover', false);
    }
  }, [isSectionRotationAndTakeoverDisabled, setValue]);

  // True when the screen configuration does not support ThankYou or Dynamic Area features:
  // either there is only one screen, or two screens with a presell menu layout.
  const isThankYouAndDyrUnsupported =
    screenCount < 2 ||
    (screenCount === 2 &&
      globalComponentsScreen?.screenLayoutType ===
        ScreenLayoutTypeEnum.PRESELL_MENU);

  const isCustomerOrderDisplayDisabled =
    codGrid !== IGridOptions.GRID_3X4 || !disclaimers1;

  const isRecommendationsDisabled =
    !enableCustomerOrderDisplay ||
    recommendationsGrid !== IGridOptions.GRID_3X4 ||
    !disclaimers2 ||
    !hasDYR;

  const isSuggestionsDisabled = !enableCustomerOrderDisplay || !hasDYS;

  const isThankYouScreenDisabled = !enableCustomerOrderDisplay || !hasThankYou;

  return (
    <div>
      <Typography variant="h6">
        {lang.assemblyLayouts.advancedSettings.title}
      </Typography>
      <FormGroup
        row
        sx={{ alignItems: 'center', marginTop: 1, marginBottom: 2 }}>
        <Typography
          variant="h6"
          sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <NumberContainer>1</NumberContainer>{' '}
          {lang.assemblyLayouts.advancedSettings.defaultLayoutTitle}
        </Typography>
      </FormGroup>

      {isOutdoor && (hasCOD || hasDYR || hasThankYou) && (
        <Container>
          <Typography variant="body1">
            {lang.assemblyLayouts.advancedSettings.layoutTypes}
          </Typography>
          <FormGroup row sx={{ alignItems: 'center', marginBottom: 2 }}>
            <Typography
              color={isCustomerOrderDisplayDisabled ? 'dimgray' : ''}
              sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
              {lang.assemblyLayouts.advancedSettings.enableCustomerOrderDisplay}
            </Typography>
            {isCustomerOrderDisplayDisabled && (
              <AdvancedSettingsInfoTooltip isGlobalSettingEnabled={hasCOD} />
            )}
            <Controller
              name="enableCustomerOrderDisplay"
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <Switch
                  {...field}
                  checked={field?.value}
                  disabled={isCustomerOrderDisplayDisabled}
                />
              )}
            />
          </FormGroup>

          {!isCustomerOrderDisplayDisabled && enableCustomerOrderDisplay && (
            <div>
              <Typography variant="body1" color={theme.palette.secondary.main}>
                {lang.assemblyLayouts.advancedSettings.stepOneTitle}
              </Typography>
              <MarginFormControlLabel>
                <FormGroup row sx={{ alignItems: 'center', marginBottom: 2 }}>
                  <Typography
                    color={
                      !enableCustomerOrderDisplay
                        ? 'dimgray'
                        : theme.palette.secondary.light
                    }
                    sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                    {lang.assemblyLayouts.advancedSettings.enableDynamicArea}
                  </Typography>
                  {isRecommendationsDisabled && (
                    <AdvancedSettingsInfoTooltip
                      isGlobalSettingEnabled={
                        isThankYouAndDyrUnsupported || !disabledDYR
                      }
                    />
                  )}
                  <Controller
                    name="enableDynamicArea"
                    control={control}
                    defaultValue={false}
                    render={({ field }) => (
                      <Switch
                        {...field}
                        checked={field?.value}
                        disabled={isRecommendationsDisabled}
                      />
                    )}
                  />
                </FormGroup>
              </MarginFormControlLabel>

              <Typography
                variant="body1"
                sx={{ marginTop: 2 }}
                color={theme.palette.secondary.main}>
                {lang.assemblyLayouts.advancedSettings.stepTwoTitle}
              </Typography>
              <MarginFormControlLabel>
                <FormGroup row sx={{ alignItems: 'center' }}>
                  <Typography
                    color={
                      !enableCustomerOrderDisplay
                        ? 'dimgray'
                        : theme.palette.secondary.light
                    }
                    sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                    {
                      lang.assemblyLayouts.advancedSettings
                        .enableCodTriggeredArea
                    }
                  </Typography>
                  {isSuggestionsDisabled && (
                    <AdvancedSettingsInfoTooltip
                      isGlobalSettingEnabled={hasDYS}
                    />
                  )}
                  <Controller
                    name="enableCodTriggeredArea"
                    control={control}
                    defaultValue={false}
                    render={({ field }) => (
                      <Switch
                        {...field}
                        checked={field?.value}
                        disabled={isSuggestionsDisabled}
                      />
                    )}
                  />
                </FormGroup>
              </MarginFormControlLabel>

              <MarginFormControlLabel>
                <FormGroup row sx={{ alignItems: 'center', marginBottom: 2 }}>
                  <Typography
                    color={
                      !enableCustomerOrderDisplay
                        ? 'dimgray'
                        : theme.palette.secondary.light
                    }
                    sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                    {lang.assemblyLayouts.advancedSettings.enablePromos}
                  </Typography>
                  <Controller
                    name="enablePromos"
                    control={control}
                    defaultValue={false}
                    render={({ field }) => (
                      <Switch
                        {...field}
                        checked={field?.value}
                        disabled={!enableCustomerOrderDisplay}
                      />
                    )}
                  />
                </FormGroup>
              </MarginFormControlLabel>

              <Typography
                variant="body1"
                sx={{ marginTop: 2 }}
                color={theme.palette.secondary.main}>
                {lang.assemblyLayouts.advancedSettings.stepThreeTitle}
              </Typography>
              <MarginFormControlLabel>
                <FormGroup row sx={{ alignItems: 'center', marginBottom: 2 }}>
                  <Typography
                    color={
                      !enableCustomerOrderDisplay
                        ? 'dimgray'
                        : theme.palette.secondary.light
                    }
                    sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                    {lang.assemblyLayouts.advancedSettings.enableThankYouScreen}
                  </Typography>
                  {isThankYouScreenDisabled && (
                    <AdvancedSettingsInfoTooltip
                      isGlobalSettingEnabled={isThankYouAndDyrUnsupported}
                    />
                  )}
                  <Controller
                    name="enableThankYou"
                    control={control}
                    defaultValue={false}
                    render={({ field }) => (
                      <Switch
                        {...field}
                        checked={field?.value}
                        disabled={isThankYouScreenDisabled}
                      />
                    )}
                  />
                </FormGroup>
              </MarginFormControlLabel>
            </div>
          )}
        </Container>
      )}

      <Divider sx={{ marginBottom: 2 }} />

      <FormGroup row sx={{ alignItems: 'center', marginBottom: 2 }}>
        <Typography
          variant="h6"
          color={isSectionRotationAndTakeoverDisabled ? 'dimgray' : ''}
          sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <NumberContainer selected={!enableRotation}>2</NumberContainer>{' '}
          {lang.assemblyLayouts.advancedSettings.enableRotation}
        </Typography>
        {isSectionRotationAndTakeoverDisabled && (
          <AdvancedSettingsInfoTooltip
            titleOverride={
              lang.assemblyLayouts.advancedSettings
                .sectionRotationDisabledTooltip
            }
          />
        )}
        <Controller
          name="enableRotation"
          control={control}
          defaultValue={false}
          render={({ field }) => (
            <span data-testid="section-rotation-toggle">
              <Switch
                {...field}
                checked={field?.value}
                disabled={isSectionRotationAndTakeoverDisabled}
              />
            </span>
          )}
        />
      </FormGroup>
      {enableCustomerOrderDisplay &&
        enableRotation &&
        !isSectionRotationAndTakeoverDisabled && (
          <AlertMessage
            severity="warning"
            icon={false}
            data-testid="Enable Rotation Alert">
            {lang.assemblyLayouts.advancedSettings.message}
          </AlertMessage>
        )}
      <Divider sx={{ marginBottom: 2 }} />

      <FormGroup row sx={{ alignItems: 'center', marginBottom: 2 }}>
        <Typography
          variant="h6"
          color={isSectionRotationAndTakeoverDisabled ? 'dimgray' : ''}
          sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <NumberContainer selected={!enableTakeover}>3</NumberContainer>{' '}
          {lang.assemblyLayouts.advancedSettings.enableTakeover}
        </Typography>
        {isSectionRotationAndTakeoverDisabled && (
          <AdvancedSettingsInfoTooltip
            titleOverride={
              lang.assemblyLayouts.advancedSettings.takeOverDisabledTooltip
            }
          />
        )}
        <Controller
          name="enableTakeover"
          control={control}
          defaultValue={false}
          render={({ field }) => (
            <span data-testid="takeover-toggle">
              <Switch
                {...field}
                checked={field?.value}
                disabled={isSectionRotationAndTakeoverDisabled}
              />
            </span>
          )}
        />
      </FormGroup>
      {enableCustomerOrderDisplay &&
        enableTakeover &&
        !isSectionRotationAndTakeoverDisabled && (
          <AlertMessage
            severity="warning"
            icon={false}
            data-testid="Enable Takeover Alert">
            {lang.assemblyLayouts.advancedSettings.message}
          </AlertMessage>
        )}
      <Divider sx={{ marginBottom: 2 }} />
    </div>
  );
};
